import { useSyncExternalStore } from 'react';
import {
  applyReply,
  expandUnits,
  resolveOps,
  type Item,
  type ItemId,
  type PersonId,
  type Receipt,
  type ResolveResult,
  type SplitContext,
  type SplitHistory,
  type SplitOp,
  type SplitOutcome,
  type SplitReply,
  validateOps,
} from '@bill/core';

/**
 * The whole flow's state, in one place, in memory.
 *
 * Mirrored to sessionStorage so a mobile browser evicting the tab (which
 * happens the moment the user switches to the camera app and back) does not
 * throw away a receipt that took ten seconds and one API call to read. The
 * downscaled photo goes in too, and is the first thing dropped if the
 * storage quota is hit.
 *
 * There is no roster. The people come out of the conversation: every message
 * the user sends is a turn, the model's reply to the latest one is stored
 * raw, and who is at the table is re-derived from it plus the user's answers
 * (head count, renames, "who had the X?"). A correction is just another turn;
 * the model sees the whole conversation so far as one sentence.
 */
export interface Turn {
  text: string;
  reply?: SplitReply;
  error?: string;
}

export interface Session {
  /** The downscaled photo as sent to the model; a few hundred KB, kept for the viewer. */
  image?: { base64: string; mimeType: string; width?: number; height?: number };
  hasPhoto: boolean;
  receipt?: Receipt;
  /** The user tapped "looks right" on the receipt. */
  confirmed: boolean;
  /** Why the last photo could not be read, shown as an app message. */
  readError?: string;
  /** The speaker's display name: "You" unless they named themselves in a sentence. */
  myName: string;
  turns: Turn[];
  /** Answer to "how many of you were there?" */
  headCount?: number;
  /** Renames of guests (or anyone), keyed by final person id. */
  renames: Record<PersonId, string>;
  /** Answers to "who had the X?" for items the sentence left out. */
  manual: Record<ItemId, PersonId[]>;
  extrasRule: 'proportional' | 'equal';
}

export const SPEAKER_ID: PersonId = 'p1';
const NAME_KEY = 'bill-splitter:my-name';
const STORAGE_KEY = 'bill-splitter:session';

export const DEFAULT_NAME = 'You';

function loadName(): string {
  try {
    const stored = localStorage.getItem(NAME_KEY);
    return stored && stored !== 'Me' ? stored : DEFAULT_NAME;
  } catch {
    return DEFAULT_NAME;
  }
}

const EMPTY: Session = {
  myName: DEFAULT_NAME,
  hasPhoto: false,
  confirmed: false,
  turns: [],
  renames: {},
  manual: {},
  extrasRule: 'proportional',
};

function load(): Session {
  const myName = loadName();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY, myName };
    const parsed = JSON.parse(raw) as Partial<Session>;
    return { ...EMPTY, ...parsed, myName };
  } catch {
    return { ...EMPTY, myName };
  }
}

function save(session: Session): void {
  try {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      // Over quota with the photo in: keep everything but the photo.
      const { image: _image, ...rest } = session;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
    }
    localStorage.setItem(NAME_KEY, session.myName);
  } catch {
    // Private mode or quota — the in-memory copy still works for this visit.
  }
}

let current: Session = load();
const listeners = new Set<() => void>();

function emit(): void {
  save(current);
  for (const listener of listeners) listener();
}

export function getSession(): Session {
  return current;
}

export function update(patch: Partial<Session> | ((prev: Session) => Partial<Session>)): void {
  const next = typeof patch === 'function' ? patch(current) : patch;
  current = { ...current, ...next };
  emit();
}

/** Forget everything about this bill. The user's own name is kept. */
export function reset(): void {
  current = { ...EMPTY, myName: current.myName };
  emit();
}

/**
 * Wipe everything this app ever stored in the browser — session, name, demo
 * flag — and reload, so the next visit is indistinguishable from a first one.
 */
export function wipeAll(): void {
  try {
    sessionStorage.clear();
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('bill-splitter:')) localStorage.removeItem(key);
    }
  } catch {
    // nothing to clear
  }
  window.location.replace('/');
}

/** Re-read the receipt: everything derived from the old one is stale. */
export function setReceipt(receipt: Receipt, image?: Session['image']): void {
  update({
    receipt,
    ...(image ? { image } : {}),
    hasPhoto: true,
    confirmed: false,
    readError: undefined,
    turns: [],
    headCount: undefined,
    manual: {},
  });
}

/** Edits on the check step keep the conversation; the numbers just change. */
export function editReceipt(receipt: Receipt): void {
  update({ receipt });
}

/**
 * Record what the user said and what the model made of it. A stated head
 * count in the new reply replaces an earlier answer; otherwise answers stand.
 */
export function addTurn(turn: Turn): void {
  update((prev) => ({
    turns: [...prev.turns, turn],
    ...(turn.reply?.groupSize !== undefined ? { headCount: undefined } : {}),
  }));
}

export function useSession(): Session {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSession,
    getSession,
  );
}

/** Everything the user has said about this bill, as one sentence for the model. */
export function fullUtterance(session: Session, extra?: string): string {
  const texts = session.turns.map((turn) => turn.text.trim()).filter(Boolean);
  if (extra && extra.trim()) texts.push(extra.trim());
  return texts.join('. ');
}

/** The latest reply the model gave, if any turn succeeded. */
export function latestReply(session: Session): SplitReply | undefined {
  for (let i = session.turns.length - 1; i >= 0; i -= 1) {
    const reply = session.turns[i]?.reply;
    if (reply) return reply;
  }
  return undefined;
}

/** The receipt's lines as the split sees them: multi-quantity lines become units. */
export function splitItems(receipt: Receipt): Item[] {
  return expandUnits(receipt.items);
}

/** What the model is told before the sentence: the items and the speaker. */
export function baseContext(session: Session): SplitContext | null {
  if (!session.receipt) return null;
  return {
    items: splitItems(session.receipt),
    people: [{ id: SPEAKER_ID, name: session.myName.trim() || DEFAULT_NAME }],
    speakerId: SPEAKER_ID,
  };
}

/**
 * The current state of the split: roster, ops, what is still unassigned and
 * whether a head count is needed. Pure function of the session, no model.
 *
 * Each reply is applied against the table the previous one produced, and its
 * ops are appended: the model is asked for what the NEW message changes, and
 * a later op overrides an earlier one for the items it touches. That is how a
 * correction lands without the model having to restate the whole split.
 */
export function deriveSplit(session: Session): SplitOutcome | null {
  const base = baseContext(session);
  if (!base) return null;
  const options = {
    ...(session.headCount !== undefined ? { headCount: session.headCount } : {}),
    renames: session.renames,
  };
  let people = base.people;
  let ops: SplitOp[] = [];
  let lastReply: SplitReply | undefined;
  let anyEveryone = false;
  let sizeKnown = false;
  const said: string[] = [];
  for (const turn of session.turns) {
    said.push(turn.text.trim());
    if (!turn.reply) continue;
    const applied = applyReply(turn.reply, { ...base, people }, said.join('. '), options);
    if (applied.errors.length > 0) return { status: 'failed', errors: applied.errors };
    people = applied.people;
    ops = [...ops, ...applied.ops];
    lastReply = turn.reply;
    anyEveryone ||= turn.reply.everyone;
    sizeKnown ||= turn.reply.everyone && !applied.askHeadCount;
  }
  if (!lastReply) return null;
  const context: SplitContext = { ...base, people };
  const bad = validateOps(ops, context);
  if (bad.length > 0) return { status: 'failed', errors: bad.map((e) => `op ${e.opIndex}: ${e.message}`) };
  return {
    status: 'ok',
    reply: lastReply,
    people,
    ops,
    resolved: resolveOps(ops, context),
    askHeadCount: anyEveryone && !sizeKnown && session.headCount === undefined,
  };
}

/** The table as the conversation stands: what the next message is parsed against. */
export function currentContext(session: Session): SplitContext | null {
  const base = baseContext(session);
  if (!base) return null;
  const outcome = deriveSplit(session);
  return outcome?.status === 'ok' ? { ...base, people: outcome.people } : base;
}

/** Earlier messages and who holds what, for the model on a follow-up turn. */
export function historyFor(session: Session): SplitHistory | undefined {
  const messages = session.turns.filter((turn) => turn.reply).map((turn) => turn.text.trim());
  if (messages.length === 0) return undefined;
  const outcome = deriveSplit(session);
  if (!outcome || outcome.status !== 'ok') return { messages, assignments: {} };
  const resolved = finalResolve(session, outcome);
  const nameOf = (id: string): string => outcome.people.find((person) => person.id === id)?.name ?? id;
  const assignments: Record<string, string[]> = {};
  for (const item of session.receipt ? splitItems(session.receipt) : []) {
    assignments[item.id] = (resolved.assignments.get(item.id) ?? []).map((share) => nameOf(share.personId));
  }
  return { messages, assignments };
}

/**
 * The model's ops plus the user's tap answers, but only for items the
 * conversation still leaves open: a later sentence always beats an earlier
 * tap, so "actually Ana had the coke" is not undone by a chip pressed before.
 */
export function finalResolve(session: Session, outcome: SplitOutcome & { status: 'ok' }): ResolveResult {
  const context: SplitContext = { items: session.receipt ? splitItems(session.receipt) : [], people: outcome.people };
  const fromModel = resolveOps(outcome.ops, context);
  const stillOpen = new Set(fromModel.unassigned);
  const manual: SplitOp[] = Object.entries(session.manual)
    .filter(([itemId, ids]) => ids.length > 0 && stillOpen.has(itemId))
    .map(([itemId, ids]) => ({
      kind: 'assign',
      items: { kind: 'ids', ids: [itemId] },
      to: { kind: 'only', ids },
    }));
  return manual.length === 0 ? fromModel : resolveOps([...outcome.ops, ...manual], context);
}

/** The stored photo as something an <img> can show. */
export function photoUrl(image: Session['image']): string | undefined {
  return image ? `data:${image.mimeType};base64,${image.base64}` : undefined;
}
